import { useState, useRef } from 'react';
import {
  SpaceBetween,
  FormField,
  Input,
  Autosuggest,
  Button,
  Box,
  Container,
  Header,
  ColumnLayout,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Contact } from '@amzn/red-velvet-api';
import { OptionalLabel, EventStepProps } from './Common';
import { isValidEmail } from '../../../../utils/validation';
import { useFeatures, getEnabledFeatures } from '../../../../hooks/features';
import { searchPeople } from '../../../../utils/papiSearch';

const MAX_CONTACTS = 5;

export interface AliasSearchProps {
  suggestions: { value: string; description: string }[];
  loading: boolean;
  loadSuggestions: (filteringText: string, rowIndex: number) => void;
  getEmailForAlias: (alias: string) => string | undefined;
  activeSearchIndex: number | null;
}

export function ContactsComponent({
  currentFields,
  updateFieldHandler,
  errors,
  isReadOnly,
}: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);

  const amazonContacts = currentFields.amazonContacts ?? [{ name: '', phone: '', email: '' }];
  const externalContacts = currentFields.externalContacts ?? [{ name: '', phone: '', email: '' }];

  const updateAmazonContacts = (contacts: Contact[]) => {
    updateFieldHandler({ ...currentFields, amazonContacts: contacts });
  };

  const updateExternalContacts = (contacts: Contact[]) => {
    updateFieldHandler({ ...currentFields, externalContacts: contacts });
  };

  // PAPI alias search — lifted here so only Amazon contacts carry this state
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const [aliasSuggestions, setAliasSuggestions] = useState<
    { value: string; description: string }[]
  >([]);
  const [aliasLoading, setAliasLoading] = useState(false);
  const aliasEmailMap = useRef<Map<string, string>>(new Map());
  const activeSearchIndex = useRef<number | null>(null);
  const searchIdRef = useRef(0);

  const loadAliasSuggestions = async (filteringText: string, rowIndex: number) => {
    activeSearchIndex.current = rowIndex;
    const thisSearchId = ++searchIdRef.current;
    setAliasLoading(true);
    try {
      const people = await searchPeople(
        filteringText,
        queryClient,
        getEnabledFeatures(features, searchParams)
      );
      if (searchIdRef.current !== thisSearchId) return;
      if (people === null) {
        setAliasSuggestions([]);
        return;
      }
      people.forEach((person) => {
        if (person.alias && person.primaryEmail) {
          aliasEmailMap.current.set(person.alias, person.primaryEmail);
        }
      });
      setAliasSuggestions(
        people.map((person) => ({
          value: person.alias ?? '',
          description: `${person.lastName}, ${person.firstName}`,
        }))
      );
    } catch {
      if (searchIdRef.current === thisSearchId) setAliasSuggestions([]);
    } finally {
      if (searchIdRef.current === thisSearchId) setAliasLoading(false);
    }
  };

  const aliasSearch: AliasSearchProps = {
    suggestions: aliasSuggestions,
    loading: aliasLoading,
    loadSuggestions: loadAliasSuggestions,
    getEmailForAlias: (alias) => aliasEmailMap.current.get(alias),
    activeSearchIndex: activeSearchIndex.current,
  };

  return (
    <Container header={<Header variant="h2">{t('teamPartnershipStep.contacts')}</Header>}>
      <SpaceBetween size="xl">
        <ContactSection
          title={t('teamPartnershipStep.amazonContactsTitle')}
          description={t('teamPartnershipStep.amazonContactsDescription')}
          contacts={amazonContacts}
          onUpdate={updateAmazonContacts}
          addLabel={t('teamPartnershipStep.addAmazonContact')}
          nameLabel={t('teamPartnershipStep.contactNameAlias')}
          namePlaceholder={t('teamPartnershipStep.contactNameAliasPlaceholder')}
          showEmail={false}
          aliasSearch={aliasSearch}
          errors={errors}
          errorPrefix="amazonContact"
          isReadOnly={isReadOnly}
        />
        <ContactSection
          title={t('teamPartnershipStep.externalContactsTitle')}
          description={t('teamPartnershipStep.externalContactsDescription')}
          contacts={externalContacts}
          onUpdate={updateExternalContacts}
          addLabel={t('teamPartnershipStep.addExternalContact')}
          nameLabel={t('teamPartnershipStep.contactName')}
          namePlaceholder={t('teamPartnershipStep.contactNamePlaceholder')}
          showEmail={true}
          errors={errors}
          errorPrefix="externalContact"
          isReadOnly={isReadOnly}
        />
      </SpaceBetween>
    </Container>
  );
}

interface PendingInput {
  index: number;
  field: keyof Contact;
  value: string;
}

function ContactSection({
  title,
  description,
  contacts,
  onUpdate,
  addLabel,
  nameLabel,
  namePlaceholder,
  showEmail,
  aliasSearch,
  errors,
  errorPrefix,
  isReadOnly,
}: {
  title: string;
  description: string;
  contacts: Contact[];
  onUpdate: (contacts: Contact[]) => void;
  addLabel: string;
  nameLabel: string;
  namePlaceholder: string;
  showEmail: boolean;
  aliasSearch?: AliasSearchProps;
  errors?: Record<string, string>;
  errorPrefix: string;
  isReadOnly?: boolean;
}) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [pendingInput, setPendingInput] = useState<PendingInput | null>(null);
  const nextId = useRef(contacts.length);
  const contactRowKeys = useRef<number[]>(contacts.map((_, i) => i));

  // Keep React keys in sync when the parent provides a different contacts array
  // (e.g., loading a previously saved draft or restoring wizard state).
  if (contactRowKeys.current.length < contacts.length) {
    while (contactRowKeys.current.length < contacts.length) {
      contactRowKeys.current.push(nextId.current++);
    }
  } else if (contactRowKeys.current.length > contacts.length) {
    contactRowKeys.current.length = contacts.length;
  }

  const mergedErrors = { ...localErrors, ...errors };

  const getDisplayValue = (index: number, field: keyof Contact): string => {
    if (pendingInput?.index === index && pendingInput.field === field) return pendingInput.value;
    return contacts[index]?.[field] ?? '';
  };

  const handleChange = (index: number, field: keyof Contact, value: string) => {
    if (field === 'phone') value = value.replace(/\D/g, '').slice(0, 15);
    if (field === 'email') value = value.replace(/\s/g, '');
    if (field === 'name' && aliasSearch) aliasSearch.loadSuggestions(value, index);
    setPendingInput({ index, field, value });
  };

  const handleBlur = (index: number, field: keyof Contact) => {
    if (pendingInput?.index === index && pendingInput.field === field) {
      const trimmedValue = pendingInput.value.trim();
      const updated = [...contacts];
      updated[index] = { ...contacts[index], [field]: trimmedValue };
      if (aliasSearch && field === 'name') {
        const email = aliasSearch.getEmailForAlias(trimmedValue);
        if (email) {
          updated[index] = { ...updated[index], email };
        } else {
          // Clear unvalidated free-text — alias must be selected from PAPI suggestions
          updated[index] = { ...updated[index], name: '', email: '' };
        }
      }
      onUpdate(updated);
      setPendingInput(null);
    }
    validateOnBlur(index, field, getDisplayValue(index, field));
  };

  const validateOnBlur = (index: number, field: keyof Contact, value: string) => {
    const key = `${errorPrefix}_${index}_${field}`;
    if (field === 'email' && value && !isValidEmail(value)) {
      setLocalErrors((prev) => ({ ...prev, [key]: t('error.invalidEmail') }));
    } else {
      setLocalErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const addContact = () => {
    if (contacts.length < MAX_CONTACTS) {
      contactRowKeys.current.push(nextId.current++);
      onUpdate([...contacts, { name: '', phone: '', email: '' }]);
    }
  };

  const removeContact = (index: number) => {
    contactRowKeys.current.splice(index, 1);
    if (pendingInput?.index === index) setPendingInput(null);
    onUpdate(contacts.filter((_, i) => i !== index));
  };

  const renderNameField = (index: number) => {
    if (aliasSearch) {
      const isActiveRow = aliasSearch.activeSearchIndex === index;
      return (
        <Autosuggest
          filteringType="manual"
          options={isActiveRow ? aliasSearch.suggestions : []}
          statusType={isActiveRow && aliasSearch.loading ? 'loading' : undefined}
          placeholder={namePlaceholder}
          value={getDisplayValue(index, 'name')}
          onChange={({ detail }) => handleChange(index, 'name', detail.value)}
          onBlur={() => handleBlur(index, 'name')}
          readOnly={isReadOnly}
        />
      );
    }
    return (
      <Input
        placeholder={namePlaceholder}
        value={getDisplayValue(index, 'name')}
        onChange={({ detail }) => handleChange(index, 'name', detail.value)}
        onBlur={() => handleBlur(index, 'name')}
        readOnly={isReadOnly}
      />
    );
  };

  return (
    <SpaceBetween size="m">
      <Box>
        <Header description={description} variant="h3">
          {title}
        </Header>
      </Box>
      <SpaceBetween size="s">
        {contacts.map((contact, index) => (
          <ColumnLayout
            key={contactRowKeys.current[index]}
            columns={contacts.length > 1 && !isReadOnly ? (showEmail ? 4 : 3) : showEmail ? 3 : 2}
          >
            <FormField label={nameLabel} errorText={mergedErrors?.[`${errorPrefix}_${index}_name`]}>
              {renderNameField(index)}
            </FormField>
            {showEmail && (
              <FormField
                label={t('teamPartnershipStep.contactEmail')}
                errorText={mergedErrors?.[`${errorPrefix}_${index}_email`]}
              >
                <Input
                  placeholder={t('teamPartnershipStep.contactEmailPlaceholder')}
                  type="email"
                  value={getDisplayValue(index, 'email')}
                  onChange={({ detail }) => handleChange(index, 'email', detail.value)}
                  onBlur={() => handleBlur(index, 'email')}
                  readOnly={isReadOnly}
                />
              </FormField>
            )}
            <FormField
              label={<OptionalLabel>{t('teamPartnershipStep.contactPhone')}</OptionalLabel>}
              errorText={mergedErrors?.[`${errorPrefix}_${index}_phone`]}
            >
              <Input
                placeholder={t('teamPartnershipStep.contactPhonePlaceholder')}
                value={getDisplayValue(index, 'phone')}
                onChange={({ detail }) => handleChange(index, 'phone', detail.value)}
                onBlur={() => handleBlur(index, 'phone')}
                readOnly={isReadOnly}
              />
            </FormField>
            {contacts.length > 1 && !isReadOnly && (
              /* Hack for invisible label to vertically align remove button with text inputs */
              <FormField label="&nbsp;">
                <Button variant="normal" onClick={() => removeContact(index)}>
                  {t('teamPartnershipStep.remove')}
                </Button>
              </FormField>
            )}
          </ColumnLayout>
        ))}
      </SpaceBetween>
      {!isReadOnly && (
        <SpaceBetween size="xs">
          <Button
            iconName="add-plus"
            variant="normal"
            onClick={addContact}
            disabled={contacts.length >= MAX_CONTACTS}
          >
            {addLabel}
          </Button>
          <Box color="text-body-secondary" variant="small">
            {t('teamPartnershipStep.maxContacts', { max: MAX_CONTACTS })}
          </Box>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
